import dayjs from "dayjs";
import { getInvalidRangeErrorMessage, isIPValid, showToastMessage } from "../../../../utils/commonFunctions";
import { FormDetailsType, FormHWILType, FormScheduleType, ScenarioFormDataType } from "./scenario-types";
import { isEqual, sortBy } from "lodash";
import { SCENARIO_STEPS, SCENARIO_DEPLOYMENT_TYPES } from "../../constants";
import { ScenarioResponseType } from "../../types";

export const validateScenarioTime = (date: number) => {
    // check the date is valid or not
    const scenarioDuration = 1440 * 60
    if (!date) {
        showToastMessage('info', "Please enter valid scenario start time")
        return false
    } else if (dayjs.utc(date * 1000).year() < 2002 || dayjs.utc((date + scenarioDuration) * 1000).year() > 2037) {
        showToastMessage('info', "Select a start time between January 1, 2002 and December 30, 2037")
        return false
    } else {
        return true
    }
}

export const validateSimulationTime = (date: number, duration: number | string, scenarioStartTime: number, scenarioDuration: number) => {
    // check the date is valid or not
    if (!date) {
        showToastMessage('info', "Please enter valid simulation start time")
        return false
    } else if (!duration || duration as number < 10 || duration as number > scenarioDuration) {
        const scenarioEndTime = scenarioStartTime + (scenarioDuration * 60)
        const remainingDuration = Math.floor((scenarioEndTime - date) / 60)
        showToastMessage('info', !duration ? "Please enter valid duration" : getInvalidRangeErrorMessage('Duration', 10, remainingDuration, 'minutes'))
        return false
    } else if (date < scenarioStartTime || (date + (duration as number) * 60) > (scenarioStartTime + (scenarioDuration as number) * 60)) {
        showToastMessage('info', (date < scenarioStartTime) ? "Simulation start time must be greater than scenario start time" : "The simulation window must be limited to a 24-hour period from the scenario's start time")
        return false
    } else {
        return true
    }
}

const validateScenarioDetails = (details: FormDetailsType) => {
    if (!details.scenarioName) {
        showToastMessage('info', "Please enter scenario title")
        return false
    } else if (!validateScenarioTime(details.scenarioStartTime)) {
        return false
    }
    return true
}

export const validateSameScenarioDetails = (details: FormDetailsType, scenario: ScenarioResponseType) => {
    if (details?.scenarioName !== scenario.scenarioName) {
        return false
    } else if (details?.scenarioStartTime !== scenario.scenarioStartTime) {
        return false
    } else if (details.scenarioOperation !== scenario.scenarioOperation) {
        return false
    } else if (details.scenarioDeploymentType !== scenario.scenarioDeploymentType) {
        return false
    } else if (details.sampleRate !== scenario.sampleRate) {
        return false
    }
    return true
}

export const validateScenarioSatellites = (satellites: ScenarioFormDataType['satellites']) => {
    if (!satellites?.length) {
        showToastMessage('info', "Please select at least one satellite")
        return false
    }
    return true
}

export const validateSameScenarioSatellites = (satellites: ScenarioFormDataType['satellites'], scenario: ScenarioResponseType) => {
    if (!scenario?.satelliteGroups?.length) return true

    const formSatellites = satellites?.map(satellite => ({
        groupName: satellite?.groupName,
        groupId: satellite?.groupId,
        satelliteId: satellite?.satelliteId,
        satelliteName: satellite?.satelliteName,
        phasingAngle: satellite?.phasingAngle || 0,
        referenceOrbit: satellite?.referenceOrbit,
    })) || []

    const scenarioSatellites = scenario?.satelliteGroups?.map(group => ({
        groupName: group.satelliteGroupName,
        groupId: group?.satelliteGroupId,
        satelliteId: scenario.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? group?.satellites[0]?.satelliteId : '',
        satelliteName: scenario.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP ? group?.satellites[0]?.satelliteName : '',
        phasingAngle: group?.satellitePhasingAngle || 0,
        referenceOrbit: group?.groupReferenceOrbit,
    }))
    const same = isEqual(sortBy(formSatellites, 'groupId'), sortBy(scenarioSatellites, 'groupId'))
    return same
}

export const validateHwilDetails = (hwilDetails: FormHWILType) => {
    const hasPayloadComponent = hwilDetails?.hwInfo?.some(hwInfo => hwInfo.category === 'Payload')
    const remoteAppConfig = hwilDetails?.remoteAppConfiguration?.remoteApp.AppData || []
    if (!hwilDetails?.hwInfo?.length) {
        showToastMessage('info', "Please select at least one Component")
        return false
    } else if (hasPayloadComponent) {
        if (!remoteAppConfig?.length) {
            showToastMessage('info', "Please configure the selected payload interfaces to continue")
            return false
        } else {
            const notValid = remoteAppConfig.some(payload => {
                const Interfaces = payload?.interfaces || [];
                const hasNoInterfaces = Interfaces.length === 0;
                const invalidCAN = Interfaces.includes('CAN') && payload?.canPath0 === '';
                const invalidEthernet = Interfaces.includes('Ethernet') && !isIPValid(payload?.payloadDeviceIP ?? '');
                return hasNoInterfaces || invalidCAN || invalidEthernet;
            });
            if (notValid) {
                showToastMessage('info', "Please configure the selected payload interfaces to continue")
                return false
            }
            return true
        }
    }
    return true
}

export const validateSameHwilDetails = (hwilDetails: FormHWILType, scenario: ScenarioResponseType) => {
    if (!scenario?.hwInfo || !scenario?.remoteAppConfiguration) return true
    const hasPayloadComponent = scenario?.hwInfo?.some(hwInfo => hwInfo.category === 'Payload')
    const formHwilInfo = hwilDetails?.hwInfo
    const formRemoteApp = {
        ...hwilDetails?.remoteAppConfiguration,
        remoteApp: hasPayloadComponent ? {
            AppData: hwilDetails?.remoteAppConfiguration?.remoteApp?.AppData.map((payload) => {
                const updatedPayload = { ...payload }
                delete updatedPayload.interfaces
                return updatedPayload
            })
        } : {}
    }
    const scenarioHwilInfo = scenario?.hwInfo
    const scenarioRemoteApp = scenario?.remoteAppConfiguration
    const formHwilObject = { hwInfo: formHwilInfo, remoteAppConfiguration: formRemoteApp }
    const scenarioHwilObject = { hwInfo: scenarioHwilInfo, remoteAppConfiguration: scenarioRemoteApp }
    const same = isEqual(formHwilObject, scenarioHwilObject)
    return same
}

export const validateInitialAltitude = (initialAltitude: ScenarioFormDataType['initial_altitude']) => {
    const orientation = initialAltitude?.orientation
    const angularRate = initialAltitude?.angularRate
    const notValidOrientation = Object.values(orientation).some(v => v === '');
    const notValidAngularRate = Object.values(angularRate).some(v => v === '');

    if (notValidOrientation) {
        showToastMessage('info', "Orientation cannot have empty fields")
        return false
    } else if (notValidAngularRate) {
        showToastMessage('info', "Angular rate cannot have empty fields")
        return false
    }
    return true
}

export const validateSameInitialAltitude = (initialAltitude: ScenarioFormDataType['initial_altitude'], scenario: ScenarioResponseType) => {
    if (!scenario?.orientation || !scenario?.angularRate) return true
    const scenarioInitialAltitude = {
        orientation: scenario?.orientation,
        angularRate: scenario?.angularRate
    }
    const same = isEqual(initialAltitude, scenarioInitialAltitude)
    return same
}

export const validateGroundStations = (groundStations: ScenarioFormDataType['segments']['stations']) => {
    if (!groundStations?.length) {
        showToastMessage('info', "Please select at least one ground station")
        return false
    }
    return true
}

export const validateSameGroundStations = (groundStations: ScenarioFormDataType['segments']['stations'], scenario: ScenarioResponseType) => {
    if (!scenario?.groundStations?.length) return true
    return isEqual(sortBy(groundStations), sortBy(scenario?.groundStations))
}

export const validateScheduleDetails = (formData: ScenarioFormDataType) => {
    const scheduleDetails: FormScheduleType = formData?.schedule
    const scenarioStartTime = formData?.details?.scenarioStartTime
    const scenarioDuration = formData?.details?.scenarioDuration
    const simulationStartTime = scheduleDetails?.simulationStartTime
    const simulationDuration = scheduleDetails?.simulationDuration
    if (!validateSimulationTime(simulationStartTime, simulationDuration, scenarioStartTime, scenarioDuration)) {
        return false
    }
    return true
}

export const validateSameScheduleDetails = (scheduleDetails: FormScheduleType, scenario: ScenarioResponseType) => {
    const simulationStartTime = scheduleDetails?.simulationStartTime
    const simulationDuration = scheduleDetails?.simulationDuration
    const scenarioSimulationStartTime = scenario?.simulationStartTime
    const scenarioSimulationDuration = scenario?.simulationDuration
    const scenarioTaskplans = scenario?.satelliteGroups.map((group) => group?.taskPlanId).filter(item => item && item.trim() !== "") || []
    const selectedTaskPlans = scheduleDetails?.taskplans.map((taskplan) => taskplan?.taskPlanId).filter(item => item && item.trim() !== "") || []
    const taskPlanChanged = scenarioTaskplans?.length !== selectedTaskPlans?.length

    if (simulationStartTime !== scenarioSimulationStartTime) {
        return false
    } else if (simulationDuration !== scenarioSimulationDuration) {
        return false
    } else if (taskPlanChanged || !isEqual(sortBy(scenarioTaskplans), sortBy(selectedTaskPlans))) {
        return false
    }
    return true
}

export const validateSameFaults = (faults: ScenarioFormDataType['faults'], scenario: ScenarioResponseType) => {
    const selectedFaults = faults?.map((fault) => fault?.faultPlanId).filter(item => item && item.trim() !== "") || []
    const scenarioFaults = scenario?.satelliteGroups.map((group) => group?.faultPlanId).filter(item => item && item.trim() !== "") || []
    const faultsChanged = scenarioFaults?.length !== selectedFaults?.length
    if (faultsChanged || !isEqual(sortBy(scenarioFaults), sortBy(selectedFaults))) {
        return false
    }
    return true
}

export const validateScenarioFormData = (step: string, formData: ScenarioFormDataType) => {
    switch (step) {
        case SCENARIO_STEPS.DETAILS:
            return validateScenarioDetails(formData?.details)
        case SCENARIO_STEPS.SATELLITES:
            return validateScenarioSatellites(formData?.satellites)
        case SCENARIO_STEPS.INITIAL_ALTITUDE:
            return validateInitialAltitude(formData?.initial_altitude)
        case SCENARIO_STEPS.HWIL:
            return validateHwilDetails(formData?.hwil)
        case SCENARIO_STEPS.SEGMENTS:
            return validateGroundStations(formData?.segments?.stations)
        case SCENARIO_STEPS.FEATURES:
            return validateScenarioDetails(formData?.details)
        case SCENARIO_STEPS.SCHEDULE:
            return validateScheduleDetails(formData)
        case SCENARIO_STEPS.FAULTS:
            return true
        default:
            return true
    }
}


export const validateSameScenarioData = (step: string, formData: ScenarioFormDataType, scenario: ScenarioResponseType | null) => {
    if (!scenario) return true
    switch (step) {
        case SCENARIO_STEPS.DETAILS:
            return validateSameScenarioDetails(formData?.details, scenario)
        case SCENARIO_STEPS.SATELLITES:
            return validateSameScenarioSatellites(formData?.satellites, scenario)
        case SCENARIO_STEPS.INITIAL_ALTITUDE:
            return validateSameInitialAltitude(formData?.initial_altitude, scenario)
        case SCENARIO_STEPS.HWIL:
            return validateSameHwilDetails(formData?.hwil, scenario)
        case SCENARIO_STEPS.SEGMENTS:
            return validateSameGroundStations(formData?.segments?.stations, scenario)
        case SCENARIO_STEPS.FEATURES:
            return validateScenarioDetails(formData?.details)
        case SCENARIO_STEPS.SCHEDULE:
            return validateSameScheduleDetails(formData?.schedule, scenario)
        case SCENARIO_STEPS.FAULTS:
            return validateSameFaults(formData?.faults, scenario)
        default:
            return true
    }
}

export const getStepsWithUnsavedChanges = (
    formData: ScenarioFormDataType,
    scenario: ScenarioResponseType | null
) => {
    if (!scenario) return SCENARIO_STEPS.DETAILS;
    const checksInOrder: { step: string; check: () => boolean }[] = [
        {
            step: SCENARIO_STEPS.DETAILS,
            check: () => validateSameScenarioDetails(formData.details, scenario),
        },
        {
            step: SCENARIO_STEPS.SATELLITES,
            check: () => validateSameScenarioSatellites(formData.satellites, scenario),
        },
        {
            step: SCENARIO_STEPS.INITIAL_ALTITUDE,
            check: () => validateSameInitialAltitude(formData.initial_altitude, scenario),
        },
        {
            step: SCENARIO_STEPS.HWIL,
            check: () => validateSameHwilDetails(formData.hwil, scenario),
        },
        {
            step: SCENARIO_STEPS.SEGMENTS,
            check: () => validateSameGroundStations(formData.segments?.stations, scenario),
        },
        {
            step: SCENARIO_STEPS.SCHEDULE,
            check: () => validateSameScheduleDetails(formData.schedule, scenario),
        },
        {
            step: SCENARIO_STEPS.FAULTS,
            check: () => validateSameFaults(formData.faults, scenario),
        },
    ];

    // run all checks in order → return first failed step
    for (const { step, check } of checksInOrder) {
        const isSame = check();
        if (!isSame) {
            return step; // this step has unsaved changes
        }
    }

    return null; // all steps clean, no unsaved changes
};

export const disabledTimelineResizableSlider = (
    formData: ScenarioFormDataType,
    scenario: ScenarioResponseType | null
) => {
    if (!scenario) return true;
    const checksInOrder: { step: string; check: () => boolean }[] = [
        {
            step: SCENARIO_STEPS.DETAILS,
            check: () => validateSameScenarioDetails(formData.details, scenario),
        },
        {
            step: SCENARIO_STEPS.SATELLITES,
            check: () => validateSameScenarioSatellites(formData.satellites, scenario),
        },
        {
            step: SCENARIO_STEPS.INITIAL_ALTITUDE,
            check: () => validateSameInitialAltitude(formData.initial_altitude, scenario),
        },
        {
            step: SCENARIO_STEPS.HWIL,
            check: () => validateSameHwilDetails(formData.hwil, scenario),
        },
        {
            step: SCENARIO_STEPS.SEGMENTS,
            check: () => scenario?.groundStations?.length > 0 && validateSameGroundStations(formData.segments?.stations, scenario),
        }
    ];

    // run all checks in order → return first failed step
    for (const { step, check } of checksInOrder) {
        const isSame = check();
        if (!isSame) {
            return true; // this step has unsaved changes
        }
    }

    return false; // all steps clean, no unsaved changes
};